import React, { memo } from 'react';
import { styled } from '@compiled/react';
import { lazy } from 'react-loosely-lazy';
import { BUSINESS_LIST } from '@atlassian/jira-common-constants/src/analytics-sources';
import { VIEW_MODE_SIDEBAR } from '@atlassian/jira-issue-context-service';
import { IssueBoundary } from '@atlassian/jira-issue-view/async';
import type IssueAppType from '@atlassian/jira-issue-view/src/views/issue-details/issue-app';
import Placeholder from '@atlassian/jira-placeholder';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types';
import { IssueViewSkeleton } from '@atlassian/jira-skeletons';
import { useScrollToComments } from './utils';

type Props = {
	issueKey: IssueKey;
	onClose: () => void;
};

const PACKAGE_NAME = 'jwmCommentsSidebarIssueApp';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const IssueApp = lazy<typeof IssueAppType>(
	() =>
		import(
			/* webpackChunkName: "async-issue-app" */ '@atlassian/jira-issue-view/src/views/issue-details/issue-app'
		),
);

export const CommentsSidebar = memo(({ issueKey, onClose }: Props) => {
	useScrollToComments({ issueKey });

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="businessCommentsSidebarInListView">
			<IssueBoundary packageName={PACKAGE_NAME}>
				<CSSAntiCorruptionLayer>
					<Placeholder name="issue-app" fallback={<IssueViewSkeleton />}>
						<IssueApp
							analyticsSource={BUSINESS_LIST}
							issueKey={issueKey}
							onClose={onClose}
							shouldSetInitialFocus
							shouldShowCloseButton
							shouldShowRootProjectsBreadcrumb
							isSpaEnabled={false}
							isLoadedWithPage={false}
							viewModeOptions={{
								viewMode: VIEW_MODE_SIDEBAR,
							}}
						/>
					</Placeholder>
				</CSSAntiCorruptionLayer>
			</IssueBoundary>
		</ContextualAnalyticsData>
	);
});

/**
 * Used to fix styles of Issue App in Sidebar broken by nested selectors affecting clipboard boundary
 * inline div on feature cleanup
 * remove div on SidebarContent improvement
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CSSAntiCorruptionLayer = styled.div({
	height: '100%',
	boxSizing: 'border-box',
});
